import React, {useState, useMemo, useRef, useEffect, useCallback} from 'react';
import {createPortal} from 'react-dom';
import {useTranslation} from 'react-i18next';
import {FaIcon} from "../FaIcon";
import {
    getMetricGroups,
    getSuggestedItems,
    getGroupForMetricWithCustom,
    getLabelForMetricWithCustom,
    getValueTypeBadge,
    getCleanLabel,
    countMatchingMetrics,
    metricGroupsForChannels,
    METRIC_GROUP_INFO,
    METRIC_GROUP_CUSTOM,
    METRIC_GROUP_LINE,
} from "./metric_groups";
import {PROPERTIES_FOR_METRIC} from "./metrics";
import {useUnavailableMetrics} from "../../hooks/useUnavailableMetrics";

/**
 * Click afuera de TODOS los nodos del combobox. Recibe varios refs porque el menú se dibuja
 * PORTEADO al `body` (ver `useMenuPosition`): un click adentro del menú ya no cae dentro del
 * wrapper, y como esto escucha `mousedown` —que corre ANTES del `click`— cerrar ahí desmontaría
 * el botón de la métrica y el `onClick` de selección nunca llegaría a dispararse.
 */
function useClickOutside(refs, handler, active) {
    useEffect(() => {
        if (!active) return undefined;
        const onDown = (event) => {
            const inside = refs.some((ref) => ref.current && ref.current.contains(event.target));
            if (inside) return;
            handler(event);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [refs, handler, active]);
}

// Alto ideal del menú (el mismo que tenía el `max-height` de la hoja) y aire mínimo contra los
// bordes del viewport.
const MENU_IDEAL_HEIGHT = 420;
const MENU_MIN_HEIGHT = 180;
const MENU_MIN_WIDTH = 360;
const MENU_MAX_WIDTH = 480;
const MENU_GAP = 4;
const MENU_MARGIN = 8;

/**
 * Posición del menú PORTEADO, en coordenadas de viewport (`position: fixed`).
 *
 * El menú se saca del árbol del disparador a propósito. Dibujado como hijo, queda preso del
 * stacking context de cualquier ancestro que tenga transform, opacity, filter o —la trampa que
 * nos mordió— una `animation` con `fill-mode: both`: al terminar, la animación SIGUE en efecto,
 * así que el ancestro conserva su stacking context y el `z-index` del menú sólo compite adentro
 * de él. Las filas hermanas que se pintan después lo tapan, por más z-index que tenga (fila del
 * acordeón del media plan, card 91LbCkk0). Porteado al `body` el problema no puede existir, y de
 * paso deja de recortarse dentro de contenedores con `overflow` (el caso del drawer de la línea).
 *
 * Contrapartida del `fixed`: la posición hay que recalcularla cuando algo scrollea o cambia de
 * tamaño. El listener de scroll va en CAPTURA para enterarse también del scroll de contenedores
 * internos, que no burbujea.
 */
function useMenuPosition(wrapperRef, isOpen) {
    const [position, setPosition] = useState(null);

    const place = useCallback(() => {
        const trigger = wrapperRef.current;
        if (!trigger) return;
        const rect = trigger.getBoundingClientRect();
        const viewportHeight = window.innerHeight || 0;
        const viewportWidth = window.innerWidth || 0;
        const roomBelow = viewportHeight - rect.bottom - MENU_GAP - MENU_MARGIN;
        const roomAbove = rect.top - MENU_GAP - MENU_MARGIN;
        // Abre hacia arriba sólo si abajo no entra y arriba entra MEJOR: un flip que no gana nada
        // desorienta más de lo que ayuda.
        const openUp = roomBelow < MENU_IDEAL_HEIGHT && roomAbove > roomBelow;
        const room = openUp ? roomAbove : roomBelow;
        // El piso de 180px existe para que el menú no quede como una rendija inútil, PERO no puede
        // ganarle al espacio que hay: en un viewport corto (mobile con el teclado abierto) forzar 180
        // donde entran 120 tiraba el menú fuera de la pantalla — y como es `fixed`, no hay scroll que
        // lo alcance. El piso se aplica sólo si el lado elegido puede pagarlo.
        const maxHeight = room >= MENU_MIN_HEIGHT
            ? Math.min(MENU_IDEAL_HEIGHT, room)
            : Math.max(room, 0);
        // El ancho ARRANCA en el del disparador: la hoja vieja declaraba `min-width: max(360px, 100%)`
        // y en CSS el `min-width` le gana al `max-width`, así que un campo de 600px daba un menú de
        // 600. Con un `Math.min(…, 480)` plano el menú se angostaba en las pantallas anchas — un
        // cambio visual que nadie pidió. El 480 sigue siendo el techo de lo que el menú AGREGA por su
        // cuenta, no un recorte de lo que el campo ya medía.
        const width = Math.min(
            Math.max(rect.width, MENU_MIN_WIDTH),                 // nunca más angosto que 360
            Math.max(rect.width, MENU_MAX_WIDTH),                 // el techo de 480 no recorta al campo
            Math.max(MENU_MIN_WIDTH, viewportWidth - 2 * MENU_MARGIN));
        // Alineado a la izquierda del disparador, pero sin salirse por el borde derecho.
        const left = Math.max(MENU_MARGIN,
            Math.min(rect.left, viewportWidth - MENU_MARGIN - width));
        const top = openUp ? Math.max(MENU_MARGIN, rect.top - MENU_GAP - maxHeight)
            : rect.bottom + MENU_GAP;
        setPosition({left, top, width, maxHeight, openUp});
    }, [wrapperRef]);

    useEffect(() => {
        if (!isOpen) {
            setPosition(null);
            return undefined;
        }
        place();
        window.addEventListener('scroll', place, {capture: true, passive: true});
        window.addEventListener('resize', place);
        return () => {
            window.removeEventListener('scroll', place, {capture: true});
            window.removeEventListener('resize', place);
        };
    }, [isOpen, place]);

    return position;
}

function TriggerContent({value, customMetrics, lineMetrics = [], t}) {
    if (!value) {
        return (
            <span className="metric-combobox__trigger-placeholder">
                <FaIcon iconName="search" className="metric-combobox__trigger-search-icon"/>
                {t('goal_configuration.metric_select.placeholder')}
            </span>
        );
    }
    const groupKey = getGroupForMetricWithCustom(value, customMetrics, lineMetrics);
    const info = METRIC_GROUP_INFO[groupKey];
    const label = getLabelForMetricWithCustom(value, customMetrics, lineMetrics);
    const isCustom = groupKey === METRIC_GROUP_CUSTOM;
    // Las de la línea son todas porcentajes (desvíos y % del presupuesto).
    const badge = groupKey === METRIC_GROUP_LINE ? '%' : (isCustom ? '#' : getValueTypeBadge(value));
    return (
        <span className="metric-combobox__trigger-value">
            <span
                className="metric-combobox__icon"
                style={{color: info.color, backgroundColor: `${info.color}14`}}
            >
                <FaIcon prefix={info.iconPrefix} iconName={info.iconName}/>
            </span>
            {/* El disparador corta con «…» (a 390, en el drawer de la línea, entran unos 94 px): el
                title deja leer el nombre entero. QA-VIS-axxy-20. */}
            <span className="metric-combobox__trigger-label" title={label}>{label}</span>
            <span className={`metric-combobox__badge metric-combobox__badge--${badge === '$' ? 'currency' : badge === '%' ? 'pct' : 'count'}`}>{badge}</span>
        </span>
    );
}

/**
 * `channels` (opcional): los canales de la línea cuyo objetivo se elige. Con ellos se ofrecen sólo
 * los grupos de SUS plataformas (más General y las personalizadas); sin ellos, el catálogo entero,
 * como en las pantallas que no conocen la línea. El grupo de la métrica ya elegida se muestra igual,
 * aunque no sea de la línea: esconderla dejaría el valor actual sin lugar en la lista.
 *
 * `lineMetrics` (opcional): el grupo «De la línea» (`LINE:*`), que sólo pasa el constructor de alertas
 * (Centro de Alertas v2). Sin él, el selector es el de siempre.
 */
// Fuera del componente: un `[]` por defecto en la firma es un array nuevo en cada render y rompía la memoización
// del catálogo en las pantallas que no pasan `lineMetrics` (review C-av2-fe-centro-23).
const NO_LINE_METRICS = [];

export function MetricCombobox({value, onChange, disabled = false, name, customMetrics = [], channels = null,
                                   lineMetrics = NO_LINE_METRICS}) {
    const {t} = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const [focusedIndex, setFocusedIndex] = useState(-1);

    // Se pide acá y no por props a propósito: este componente es el selector de métrica de las 5
    // pantallas que lo montan, así que pedirlo una vez adentro las cubre a todas. Es información
    // del producto —igual para cualquier agencia—, no del contexto de quien lo monta.
    const {unavailableMetrics} = useUnavailableMetrics();

    const unavailableReasonText = useCallback((unavailable) => {
        const alternative = unavailable && unavailable.suggested_alternative;
        // Sin alternativa, o con una que este catálogo no conoce: se omite la sugerencia en vez de
        // imprimir el identificador crudo («…usar «Meta_IG_profile_visits»»). Que los catálogos del
        // backend y del frontend diverjan es la causa raíz de esta card — no conviene apostar a que
        // no vuelva a pasar justo en el campo donde más se notaría.
        if (!alternative || !PROPERTIES_FOR_METRIC[alternative]) {
            return t('goal_configuration.metric_select.unavailable');
        }
        return t('goal_configuration.metric_select.unavailable_with_alternative',
                 {alternative: getCleanLabel(alternative), interpolation: {escapeValue: false}});
    }, [t]);

    const wrapperRef = useRef(null);
    const searchInputRef = useRef(null);
    const menuRef = useRef(null);
    const itemRefs = useRef([]);

    const close = useCallback(() => {
        setIsOpen(false);
        setQuery('');
        setFocusedIndex(-1);
    }, []);

    // Los refs son estables: el array se memoiza para no re-suscribir el listener en cada render.
    const outsideRefs = useMemo(() => [wrapperRef, menuRef], []);
    useClickOutside(outsideRefs, close, isOpen);

    // Posición del menú porteado (ver `useMenuPosition`). Reemplaza al viejo `scrollIntoView`:
    // antes el menú medía 420px fijos y, dentro de un contenedor de scroll más corto, quedaba
    // RECORTADO (en el drawer de la línea se veían 42 de 420px — el buscador y nada más); se
    // scrolleaba la página para que entrara. Ahora se mide el espacio real, se abre hacia arriba
    // si abajo no entra y se limita el alto: entra siempre, sin mover la página del usuario.
    const menuPosition = useMenuPosition(wrapperRef, isOpen);

    /**
     * El buscador se enfoca desde el ref CALLBACK, en el momento en que el input entra al DOM — no
     * desde un efecto sobre `isOpen`.
     *
     * El menú está porteado y sólo se monta cuando `useMenuPosition` ya midió, o sea **un render
     * después** de abrir. Un `useEffect([isOpen])` corre en el render de la apertura, cuando el
     * input todavía no existe, y ya no vuelve a correr: el buscador quedaba sin foco. Y con él se
     * caía TODO el teclado, porque el `onKeyDown` vive en el div del menú y el foco se quedaba en
     * el botón disparador, que es hermano del portal y no su ancestro — así que las flechas, Enter
     * y Escape no llegaban a ningún lado. Sobre un combobox de 81 métricas, eso es el recorrido
     * principal (abrir → escribir → Enter), y afectaba a las 5 pantallas que lo montan.
     */
    const focusSearchInput = useCallback((node) => {
        searchInputRef.current = node;
        if (node) node.focus();
    }, []);

    // La identidad del array cambia en cada render del padre; la clave de texto no.
    const channelsKey = Array.isArray(channels) ? channels.join(',') : null;
    const groupKeys = useMemo(() => {
        const allowed = channelsKey === null ? null : metricGroupsForChannels(channelsKey.split(','));
        if (allowed && value) allowed.add(getGroupForMetricWithCustom(value, customMetrics, lineMetrics));
        return allowed;
    }, [channelsKey, value, customMetrics, lineMetrics]);

    const groups = useMemo(() => getMetricGroups({query, customMetrics, unavailableMetrics, groupKeys, lineMetrics}),
        [query, customMetrics, unavailableMetrics, groupKeys, lineMetrics]);
    const suggested = useMemo(() => (query ? [] : getSuggestedItems(unavailableMetrics)), [query, unavailableMetrics]);
    const matchCount = useMemo(() => countMatchingMetrics(query, customMetrics, unavailableMetrics, groupKeys, lineMetrics),
        [query, customMetrics, unavailableMetrics, groupKeys, lineMetrics]);

    // Flat list of metricNames for keyboard navigation (suggested first, then grouped).
    // Las no medibles quedan FUERA: se ven en la lista pero no se pueden elegir, así que meterlas
    // acá haría que las flechas se detuvieran en ellas y que Enter configurara justo la que no se
    // puede configurar.
    // Cada entrada lleva su `key` por OCURRENCIA (`suggested:Costo`, `GENERAL:Costo`): una sugerida
    // aparece dos veces en el menú —el chip y su fila del grupo—, y buscar el índice por nombre les
    // daba el mismo a los dos. La ref de la fila pisaba a la del chip, así que pasar el mouse por el
    // chip «Costo» scrolleaba la lista hasta la fila «Costo» de General (07/10).
    const flatItems = useMemo(() => {
        const flat = [];
        suggested.filter((item) => !item.unavailable)
            .forEach((item) => flat.push({key: `suggested:${item.metricName}`, metricName: item.metricName}));
        groups.forEach(({groupKey, items}) => {
            items.filter((item) => !item.unavailable)
                .forEach((item) => flat.push({key: `${groupKey}:${item.metricName}`, metricName: item.metricName}));
        });
        return flat;
    }, [suggested, groups]);

    const indexByKey = useMemo(() => new Map(flatItems.map((it, index) => [it.key, index])), [flatItems]);

    useEffect(() => {
        itemRefs.current = itemRefs.current.slice(0, flatItems.length);
    }, [flatItems.length]);

    // La lista scrollea hasta lo enfocado sólo cuando el foco lo movió el TECLADO (flechas o
    // búsqueda): ahí la fila elegida tiene que verse. Con el mouse no: lo que se hoverea ya está a la
    // vista, y scrollear corre el contenido bajo un puntero quieto — la fila asomada al borde subía,
    // el mouse quedaba sobre la siguiente, que se enfocaba y volvía a scrollear.
    const scrollToFocusedRef = useRef(false);
    const moveFocusByKeyboard = (next) => {
        scrollToFocusedRef.current = true;
        setFocusedIndex(next);
    };
    const moveFocusByPointer = (index) => {
        scrollToFocusedRef.current = false;
        setFocusedIndex(index);
    };

    useEffect(() => {
        if (!scrollToFocusedRef.current) return;
        if (focusedIndex < 0 || focusedIndex >= flatItems.length) return;
        scrollToFocusedRef.current = false;
        const el = itemRefs.current[focusedIndex];
        if (el && typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({block: 'nearest'});
        }
    }, [focusedIndex, flatItems.length]);

    const handleToggle = () => {
        if (disabled) return;
        setIsOpen((prev) => !prev);
    };

    const handleSelect = (metricName) => {
        onChange(metricName);
        close();
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            close();
            return;
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            moveFocusByKeyboard((prev) => Math.min(prev + 1, flatItems.length - 1));
            return;
        }
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            moveFocusByKeyboard((prev) => Math.max(prev - 1, 0));
            return;
        }
        if (event.key === 'Enter') {
            if (focusedIndex >= 0 && focusedIndex < flatItems.length) {
                event.preventDefault();
                handleSelect(flatItems[focusedIndex].metricName);
            }
        }
    };

    const indexOf = (scope, metricName) => {
        const index = indexByKey.get(`${scope}:${metricName}`);
        return index === undefined ? -1 : index;
    };

    return (
        <div
            ref={wrapperRef}
            className={`metric-combobox ${isOpen ? 'is-open' : ''} ${disabled ? 'is-disabled' : ''}`}
        >
            <button
                type="button"
                name={name}
                className="metric-combobox__trigger form-control"
                onClick={handleToggle}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={isOpen}
            >
                <TriggerContent value={value} customMetrics={customMetrics} lineMetrics={lineMetrics} t={t}/>
                <FaIcon iconName="chevron-down" className="metric-combobox__chevron"/>
            </button>

            {isOpen && menuPosition && createPortal(
                <div className={`metric-combobox__menu ${menuPosition.openUp ? '-up' : ''}`}
                     ref={menuRef} onKeyDown={handleKeyDown}
                     style={{left: menuPosition.left, top: menuPosition.top,
                             width: menuPosition.width, maxHeight: menuPosition.maxHeight}}>
                    <div className="metric-combobox__header">
                        <div className="metric-combobox__search">
                            <FaIcon iconName="search" className="metric-combobox__search-icon"/>
                            <input
                                ref={focusSearchInput}
                                type="text"
                                className="metric-combobox__search-input"
                                placeholder={t('goal_configuration.metric_select.search_placeholder')}
                                value={query}
                                onChange={(e) => {
                                    setQuery(e.target.value);
                                    moveFocusByKeyboard(e.target.value ? 0 : -1);
                                }}
                            />
                            {query && (
                                <button
                                    type="button"
                                    className="metric-combobox__search-clear"
                                    onClick={() => {
                                        setQuery('');
                                        setFocusedIndex(-1);
                                        searchInputRef.current?.focus();
                                    }}
                                    aria-label={t('goal_configuration.metric_select.clear_search', 'Limpiar búsqueda')}
                                >
                                    <FaIcon iconName="times"/>
                                </button>
                            )}
                        </div>
                        <div className="metric-combobox__count">
                            {t('goal_configuration.metric_select.metric_count', {count: matchCount})}
                        </div>
                    </div>

                    <div className="metric-combobox__body">
                        {!query && suggested.some((item) => !item.unavailable) && (
                            <div className="metric-combobox__suggested">
                                <div className="metric-combobox__suggested-label">
                                    {t('goal_configuration.metric_select.suggested')}
                                </div>
                                <div className="metric-combobox__suggested-chips">
                                    {suggested.map((item) => {
                                        const groupKey = item.groupKey;
                                        const info = METRIC_GROUP_INFO[groupKey];
                                        const idx = indexOf('suggested', item.metricName);
                                        const isSelected = value === item.metricName;
                                        const isFocused = idx === focusedIndex;
                                        // Una sugerida que no se puede medir directamente no se sugiere: el
                                        // chip es un atajo, y un atajo que no lleva a ningún lado estorba.
                                        // En la lista de abajo sigue apareciendo, con su explicación.
                                        if (item.unavailable) return null;
                                        return (
                                            <button
                                                key={item.metricName}
                                                type="button"
                                                ref={(el) => { itemRefs.current[idx] = el; }}
                                                className={`metric-combobox__chip ${isSelected ? 'is-selected' : ''} ${isFocused ? 'is-focused' : ''}`}
                                                onClick={() => handleSelect(item.metricName)}
                                                onMouseEnter={() => moveFocusByPointer(idx)}
                                                style={isSelected ? {borderColor: info.color, color: info.color} : undefined}
                                            >
                                                {item.label}
                                                <span className="metric-combobox__chip-badge">{item.badge}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {groups.length === 0 && (
                            <div className="metric-combobox__empty">
                                <FaIcon iconName="search" className="metric-combobox__empty-icon"/>
                                <div className="metric-combobox__empty-text">
                                    {t('goal_configuration.metric_select.empty_state', {query})}
                                </div>
                            </div>
                        )}

                        {groups.map(({groupKey, info, items}) => (
                            <div key={groupKey} className="metric-combobox__group">
                                <div
                                    className="metric-combobox__group-header"
                                    style={{borderLeftColor: info.color}}
                                >
                                    <span
                                        className="metric-combobox__group-icon"
                                        style={{color: info.color}}
                                    >
                                        <FaIcon prefix={info.iconPrefix} iconName={info.iconName}/>
                                    </span>
                                    <span className="metric-combobox__group-label">
                                        {t(info.i18nKey, info.defaultLabel)}
                                    </span>
                                    {/* Cuenta lo ELEGIBLE, igual que el contador del encabezado: un
                                        número al lado de una lista promete opciones usables. */}
                                    <span className="metric-combobox__group-count">
                                        {items.filter((item) => !item.unavailable).length}
                                    </span>
                                </div>
                                <ul className="metric-combobox__list" role="listbox">
                                    {items.map((item) => {
                                        const idx = indexOf(groupKey, item.metricName);
                                        const isSelected = value === item.metricName;
                                        const isFocused = idx === focusedIndex;
                                        if (item.unavailable) {
                                            // El motivo va VISIBLE en la fila, no en un tooltip: el menú es un
                                            // contenedor con scroll y porteado al body, donde un tooltip anclado
                                            // se recorta sin aviso; y quien busca esta métrica necesita leer por
                                            // qué no está sin tener que descubrir que hay algo que hoverear.
                                            // `isSelected` se conserva: el destinatario de este aviso es justamente
                                            // quien YA tiene esa métrica configurada, y sin esto el disparador
                                            // muestra su nombre pero en la lista no hay nada elegido.
                                            return (
                                                <li key={item.metricName} role="option" aria-selected={isSelected} aria-disabled="true">
                                                    <div className={`metric-combobox__item is-unavailable ${isSelected ? 'is-selected' : ''}`}>
                                                        <span className="metric-combobox__item-bullet" aria-hidden="true"/>
                                                        <div className="metric-combobox__item-unavailable-body">
                                                            <span className="metric-combobox__item-label">{item.label}</span>
                                                            <span className="metric-combobox__item-unavailable-reason">
                                                                {unavailableReasonText(item.unavailable)}
                                                            </span>
                                                        </div>
                                                        {/* Ocupa el lugar del badge de tipo, que esta fila no dibuja, y da la
                                                            señal de «no elegible» sin depender del color: el nombre tiene que
                                                            quedar legible (AA), así que el gris no puede ser la única marca. */}
                                                        <span className="metric-combobox__item-unavailable-tag">
                                                            {t('goal_configuration.metric_select.unavailable_tag')}
                                                        </span>
                                                    </div>
                                                </li>
                                            );
                                        }
                                        return (
                                            <li key={item.metricName} role="option" aria-selected={isSelected}>
                                                <button
                                                    type="button"
                                                    ref={(el) => { itemRefs.current[idx] = el; }}
                                                    className={`metric-combobox__item ${isSelected ? 'is-selected' : ''} ${isFocused ? 'is-focused' : ''}`}
                                                    onClick={() => handleSelect(item.metricName)}
                                                    onMouseEnter={() => moveFocusByPointer(idx)}
                                                    style={isSelected ? {backgroundColor: `${info.color}14`} : undefined}
                                                >
                                                    <span
                                                        className="metric-combobox__item-bullet"
                                                        style={{backgroundColor: info.color}}
                                                        aria-hidden="true"
                                                    />
                                                    <span className="metric-combobox__item-label">{item.label}</span>
                                                    <span className={`metric-combobox__badge metric-combobox__badge--${item.badge === '$' ? 'currency' : item.badge === '%' ? 'pct' : 'count'}`}>{item.badge}</span>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>,
                document.body)}
        </div>
    );
}

export default MetricCombobox;
